// Recherche d'un client par le nom, le téléphone, l'email ou le code comptable.
//
// Sur un chantier, on tape vite et mal : pas d'accent au clavier du téléphone,
// le nom parfois avant le prénom, le numéro parfois collé, parfois espacé.
// La recherche doit pardonner tout cela, sinon elle ne sert à rien.
//
// Les règles, dans l'ordre :
//   1. ACCENTS ET CASSE IGNORÉS — « mehdi » trouve « Méhdi », « ST » trouve « St ».
//   2. CHAQUE MOT TAPÉ DOIT ÊTRE RETROUVÉ, mais peu importe où et dans quel
//      ordre : « jean dupont » et « dupont jean » donnent le même client.
//   3. UN MOT DE CHIFFRES cherche aussi dans les téléphones, espaces et points
//      retirés : « 0612 » trouve « 06 12 34 56 78 ».
//   4. LE PLUS PROBABLE EN PREMIER : un nom qui commence par ce qu'on a tapé
//      passe devant un nom qui le contient, qui passe devant un email.
//
// Fonctions PURES : aucun accès réseau ni base, donc testables ligne à ligne.

/** Le strict nécessaire pour retrouver un client. */
export interface ClientRecherchable {
  id: string;
  /** Nom tel qu'il s'affiche (particulier : prénom + nom ; pro : raison sociale). */
  nom: string;
  email: string | null;
  telephone: string | null;
  telephoneMobile: string | null;
  /** Numéro de compte client en comptabilité (C00012). */
  codeComptable: string | null;
}

/**
 * Le texte débarrassé de ses accents, de sa casse et de sa ponctuation.
 * « Élodie D'Anglade-Meyer » devient « elodie d anglade meyer ».
 */
export function normaliserTexte(texte: string | null | undefined): string {
  if (!texte) return '';
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Les seuls chiffres d'un texte : un téléphone comparable quel que soit son écriture. */
export function chiffresSeuls(texte: string | null | undefined): string {
  return (texte ?? '').replace(/\D+/g, '');
}

/** Les mots d'une recherche, normalisés ; une recherche vide n'en donne aucun. */
export function motsRecherche(saisie: string): string[] {
  const n = normaliserTexte(saisie);
  return n === '' ? [] : n.split(' ');
}

/** Ce qu'on compare, préparé une fois pour toutes. */
interface ClientIndexe {
  client: ClientRecherchable;
  nom: string;
  motsNom: string[];
  email: string;
  code: string;
  telephones: string;
}

function indexer(c: ClientRecherchable): ClientIndexe {
  const nom = normaliserTexte(c.nom);
  return {
    client: c,
    nom,
    motsNom: nom === '' ? [] : nom.split(' '),
    email: normaliserTexte(c.email),
    code: normaliserTexte(c.codeComptable),
    telephones: `${chiffresSeuls(c.telephone)} ${chiffresSeuls(c.telephoneMobile)}`,
  };
}

/**
 * Ce que vaut un mot pour ce client : 0 s'il ne le retrouve nulle part.
 * Les poids ne servent qu'à ranger la liste, leur valeur exacte n'a pas de sens
 * en soi — seul leur ordre compte.
 */
function scoreMot(i: ClientIndexe, mot: string): number {
  if (i.nom.startsWith(mot)) return 100;
  if (i.motsNom.some((m) => m.startsWith(mot))) return 60;
  if (i.code !== '' && i.code.startsWith(mot)) return 50;
  if (i.nom.includes(mot)) return 30;
  // Un mot de chiffres vaut un début de téléphone : « 0612 », « 612 »…
  if (/^\d+$/.test(mot) && i.telephones.includes(mot)) return 25;
  if (i.email.includes(mot)) return 10;
  return 0;
}

export interface ResultatRecherche {
  client: ClientRecherchable;
  score: number;
}

/**
 * Les clients qui correspondent, les plus probables en tête.
 *
 * Une recherche vide rend TOUT LE MONDE, par ordre alphabétique : le champ de
 * recherche remplace la liste déroulante, il ne doit donc jamais partir vide.
 */
export function chercherClients(
  clients: readonly ClientRecherchable[],
  saisie: string,
): ResultatRecherche[] {
  const mots = motsRecherche(saisie);
  const parNom = (a: ResultatRecherche, b: ResultatRecherche): number =>
    a.client.nom.localeCompare(b.client.nom, 'fr', { sensitivity: 'base' });

  if (mots.length === 0) {
    return clients.map((client) => ({ client, score: 0 })).sort(parNom);
  }

  const out: ResultatRecherche[] = [];
  for (const c of clients) {
    const i = indexer(c);
    let score = 0;
    let complet = true;
    for (const mot of mots) {
      const s = scoreMot(i, mot);
      // Un seul mot introuvable et le client sort : c'est ce qui permet
      // d'affiner en tapant un mot de plus, plutôt que d'élargir.
      if (s === 0) {
        complet = false;
        break;
      }
      score += s;
    }
    if (complet) out.push({ client: c, score });
  }

  return out.sort((a, b) => b.score - a.score || parNom(a, b));
}

/** Le même résultat, mais sans les scores : ce dont l'affichage a besoin. */
export function clientsTrouves(
  clients: readonly ClientRecherchable[],
  saisie: string,
): ClientRecherchable[] {
  return chercherClients(clients, saisie).map((r) => r.client);
}
